import React, { useMemo, useState } from 'react';
import {
  FRECUENCIAS_TOMA,
  PLAN_MESES_PRESETS,
  UNIDADES_DOSIS,
  buildPosologiaDesdeDosificacion,
  calcularDosificacion,
  calcularSugerenciaPlanMeses,
  formatFechaCorta,
  pluralizarUnidad
} from '../utils/tratamientoDosificacion';

export default function TratamientoDosificacionCalculator({
  dosificacion,
  onDosificacionChange,
  cantidadRecetas = 1,
  mesesActual = 1,
  fechaInicio = '',
  posologia = '',
  onPosologiaChange,
  onMesesChange,
  onCantidadChange,
  disabled = false
}) {
  const { dosisPorToma, unidadDosis, tomasPorDia, unidadesPorReceta } = dosificacion || {};
  const [planAplicado, setPlanAplicado] = useState(null);
  const [planFlash, setPlanFlash] = useState(false);

  const calculo = useMemo(
    () => calcularDosificacion({
      dosisPorToma,
      tomasPorDia,
      unidadesPorReceta,
      cantidadRecetas,
      fechaInicio
    }),
    [dosisPorToma, tomasPorDia, unidadesPorReceta, cantidadRecetas, fechaInicio]
  );

  const planesSugeridos = useMemo(
    () => PLAN_MESES_PRESETS.map((meses) => ({
      meses,
      plan: calcularSugerenciaPlanMeses({
        dosisPorToma,
        unidadDosis,
        tomasPorDia,
        unidadesPorReceta,
        mesesObjetivo: meses,
        fechaInicio
      })
    })).filter((item) => item.plan),
    [dosisPorToma, unidadDosis, tomasPorDia, unidadesPorReceta, fechaInicio]
  );

  const patch = (partial) => {
    onDosificacionChange?.({ ...dosificacion, ...partial });
    setPlanAplicado(null);
  };

  const aplicarPosologia = () => {
    const texto = buildPosologiaDesdeDosificacion({
      dosisPorToma,
      unidadDosis,
      tomasPorDia,
      unidadesPorReceta
    });
    if (texto) onPosologiaChange?.(texto);
  };

  const aplicarPlanMeses = (mesesObjetivo) => {
    const sug = calcularSugerenciaPlanMeses({
      dosisPorToma,
      unidadDosis,
      tomasPorDia,
      unidadesPorReceta,
      mesesObjetivo,
      fechaInicio
    });
    if (!sug) return;

    onMesesChange?.(String(sug.mesesEfectivos));
    onCantidadChange?.(String(sug.cantidadRecetas));
    if (sug.posologia) onPosologiaChange?.(sug.posologia);

    setPlanAplicado(mesesObjetivo);
    setPlanFlash(true);
    window.setTimeout(() => setPlanFlash(false), 1400);
  };

  const unidadLabel = pluralizarUnidad(unidadDosis, 2);
  const posologiaSugerida = buildPosologiaDesdeDosificacion({
    dosisPorToma,
    unidadDosis,
    tomasPorDia,
    unidadesPorReceta
  });
  const posologiaDistinta = posologiaSugerida
    && String(posologia || '').trim().toLowerCase() !== posologiaSugerida.toLowerCase();

  const planActivo = planesSugeridos.find((item) => (
    Number(item.meses) === Number(planAplicado)
    || (
      !planAplicado
      && Number(item.plan?.mesesEfectivos) === Number(mesesActual)
      && Number(item.plan?.cantidadRecetas) === Number(cantidadRecetas)
    )
  ))?.plan || null;

  return (
    <section className="crm-ta-dosi" aria-label="Calculadora de duración del tratamiento">
      <div className="crm-ta-dosi__head">
        <div>
          <span className="crm-ta-dosi__eyebrow">Planificación clínica</span>
          <h3 className="crm-ta-dosi__title">Calculadora de duración</h3>
          <p className="crm-ta-dosi__lead">
            Estimá cuánto consume el paciente por día y cuánto dura cada receta antes de definir el período.
          </p>
        </div>
        <div className="crm-ta-dosi__icon" aria-hidden="true">⏱</div>
      </div>

      <div className="crm-ta-dosi__grid">
        <div className="crm-ta-dosi__row-dosis">
          <div className="crm-ta-dosi__field crm-ta-dosi__field--dosis">
            <label htmlFor="ta-dosi-cant">Dosis por toma</label>
            <div className="crm-ta-dosi__input-wrap">
              <input
                id="ta-dosi-cant"
                type="number"
                min={0.25}
                max={99}
                step={0.25}
                value={dosisPorToma}
                onChange={(e) => patch({ dosisPorToma: e.target.value })}
                disabled={disabled}
              />
              <select
                aria-label="Unidad de dosis"
                value={unidadDosis}
                onChange={(e) => patch({ unidadDosis: e.target.value })}
                disabled={disabled}
                style={{marginLeft: '20px', width: '93%'}}
              >
                {UNIDADES_DOSIS.map((u) => (
                  <option key={u.value} value={u.value}>{u.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="crm-ta-dosi__field crm-ta-dosi__field--unidades">
            <label htmlFor="ta-dosi-unidades">
              Unidades por receta
              <span className="crm-ta-dosi__label-hint">({unidadLabel})</span>
            </label>
            <input
              id="ta-dosi-unidades"
              type="number"
              min={1}
              max={9999}
              step={1}
              placeholder="30"
              value={unidadesPorReceta}
              onChange={(e) => patch({ unidadesPorReceta: e.target.value })}
              disabled={disabled}
              style={{ width: 'calc(100% - 20px)' }}
            />
          </div>
        </div>

        <div className="crm-ta-dosi__field crm-ta-dosi__field--wide">
          <span className="crm-ta-dosi__field-label">Frecuencia de toma</span>
          <div className="crm-ta-dosi__freq" role="group" aria-label="Frecuencia de toma">
            {FRECUENCIAS_TOMA.map((f) => {
              const active = String(tomasPorDia) === String(f.value);
              return (
                <button
                  key={f.value}
                  type="button"
                  className={`crm-ta-dosi__freq-btn${active ? ' crm-ta-dosi__freq-btn--active' : ''}`}
                  onClick={() => patch({ tomasPorDia: String(f.value) })}
                  disabled={disabled}
                  title={f.sub}
                >
                  <span className="crm-ta-dosi__freq-main">{f.label}</span>
                  <span className="crm-ta-dosi__freq-sub">{f.sub}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {calculo ? (
        <div className="crm-ta-dosi__results">
          <div className="crm-ta-dosi__metrics">
            <div className="crm-ta-dosi__metric">
              <span className="crm-ta-dosi__metric-k">Consumo diario</span>
              <strong className="crm-ta-dosi__metric-v">
                {calculo.consumoDiario}
                <small> {pluralizarUnidad(unidadDosis, calculo.consumoDiario)}/día</small>
              </strong>
            </div>
            <div className="crm-ta-dosi__metric crm-ta-dosi__metric--accent">
              <span className="crm-ta-dosi__metric-k">Duración por receta</span>
              <strong className="crm-ta-dosi__metric-v">
                {calculo.duracionRecetaDias}
                <small> días</small>
              </strong>
              <span className="crm-ta-dosi__metric-sub">≈ {calculo.semanasReceta} semanas</span>
            </div>
            <div className="crm-ta-dosi__metric">
              <span className="crm-ta-dosi__metric-k">Fin estimado (1ª receta)</span>
              <strong className="crm-ta-dosi__metric-v crm-ta-dosi__metric-v--date">
                {formatFechaCorta(calculo.fechaFinReceta)}
              </strong>
            </div>
            {Number(cantidadRecetas) > 1 ? (
              <div className="crm-ta-dosi__metric crm-ta-dosi__metric--plan">
                <span className="crm-ta-dosi__metric-k">Plan completo ({cantidadRecetas} recetas)</span>
                <strong className="crm-ta-dosi__metric-v">
                  {calculo.duracionPlanDias}
                  <small> días</small>
                </strong>
                <span className="crm-ta-dosi__metric-sub">
                  hasta {formatFechaCorta(calculo.fechaFinPlan)} · {calculo.mesesSugeridos} mes(es)
                </span>
              </div>
            ) : null}
          </div>

          <div className="crm-ta-dosi__bar-wrap">
            <div className="crm-ta-dosi__bar-label">
              <span>Cobertura de 1 mes con esta receta</span>
              <strong>{calculo.coberturaMesPct}%</strong>
            </div>
            <div className="crm-ta-dosi__bar" role="presentation">
              <div
                className="crm-ta-dosi__bar-fill"
                style={{ width: `${calculo.coberturaMesPct}%` }}
              />
            </div>
          </div>

          {planesSugeridos.length ? (
            <div className={`crm-ta-dosi__planner${planFlash ? ' crm-ta-dosi__planner--flash' : ''}`}>
              <div className="crm-ta-dosi__planner-head">
                <div>
                  <span className="crm-ta-dosi__planner-eyebrow">Plan inteligente</span>
                  <h4 className="crm-ta-dosi__planner-title">Cubrir tratamiento por período</h4>
                  <p className="crm-ta-dosi__planner-lead">
                    Cada receta dura <strong>{calculo.duracionRecetaDias} días</strong>.
                    Elegí el período y completamos meses, cantidad de recetas y posología.
                  </p>
                </div>
                <span className="crm-ta-dosi__planner-badge" aria-hidden="true">✦</span>
              </div>

              <div className="crm-ta-dosi__period-grid" role="group" aria-label="Sugerir período de tratamiento">
                {planesSugeridos.map(({ meses, plan }) => {
                  const isActive = Number(planAplicado) === meses
                    || (
                      !planAplicado
                      && Number(plan.mesesEfectivos) === Number(mesesActual)
                      && Number(plan.cantidadRecetas) === Number(cantidadRecetas)
                    );
                  return (
                    <button
                      key={meses}
                      type="button"
                      className={`crm-ta-dosi__period-chip${isActive ? ' crm-ta-dosi__period-chip--active' : ''}`}
                      onClick={() => aplicarPlanMeses(meses)}
                      disabled={disabled}
                      title={`${plan.cantidadRecetas} recetas · ${plan.duracionPlanDias} días de cobertura`}
                    >
                      <span className="crm-ta-dosi__period-chip-mes">
                        {meses} mes{meses === 1 ? '' : 'es'}
                      </span>
                      <span className="crm-ta-dosi__period-chip-meta">
                        {plan.cantidadRecetas} receta{plan.cantidadRecetas === 1 ? '' : 's'}
                      </span>
                      <span className="crm-ta-dosi__period-chip-sub">
                        {plan.duracionPlanDias} d · {plan.coberturaObjetivoPct}% del objetivo
                      </span>
                    </button>
                  );
                })}
              </div>

              {planActivo ? (
                <div className="crm-ta-dosi__planner-applied">
                  <span className="crm-ta-dosi__planner-applied-icon" aria-hidden="true">✓</span>
                  <div>
                    <strong>Plan aplicado</strong>
                    <span>
                      {planActivo.mesesEfectivos} mes(es) · {planActivo.cantidadRecetas} recetas
                      {planActivo.fechaFinPlan ? ` · hasta ${formatFechaCorta(planActivo.fechaFinPlan)}` : ''}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="crm-ta-dosi__planner-hint">
                  Con la dosificación actual, 1 mes requiere{' '}
                  <strong>{planesSugeridos[0]?.plan?.cantidadRecetas || '—'} recetas</strong>
                  {' '}(no solo cambiar el número de meses).
                </p>
              )}
            </div>
          ) : null}

          <div className="crm-ta-dosi__actions">
            <button
              type="button"
              className="crm-ta-dosi__btn crm-ta-dosi__btn--primary"
              onClick={aplicarPosologia}
              disabled={disabled || !posologiaSugerida}
            >
              Aplicar a posología
            </button>
            <button
              type="button"
              className="crm-ta-dosi__btn crm-ta-dosi__btn--accent"
              onClick={() => aplicarPlanMeses(1)}
              disabled={disabled || !planesSugeridos.length}
            >
              Sugerir 1 mes completo
            </button>
          </div>

          {posologiaDistinta && posologiaSugerida ? (
            <p className="crm-ta-dosi__hint">
              Posología sugerida: <em>{posologiaSugerida}</em>
            </p>
          ) : null}
        </div>
      ) : (
        <p className="crm-ta-dosi__empty">
          Completá dosis, frecuencia y unidades por receta para ver la duración estimada.
        </p>
      )}
    </section>
  );
}
